/**
 * Hooks for Pending Identification Reviews
 *
 * Manages state and API calls for the manual operator identification
 * review system when face recognition confidence is too low.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from '../lib/hooks/useApi';

// Types
export interface PendingCandidate {
  operatorId: string;
  operatorName: string;
  similarity: number;
  photoUrl?: string;
}

export interface PendingIdentification {
  id: string;
  faceIdentificationId: string;
  videoClipId: string;
  cameraId: string;
  cameraName: string;
  capturedAt: string;
  topCandidates: PendingCandidate[];
  frameUrl?: string;
  videoUrl?: string;
  createdAt: string;
}

export interface PendingIdentificationDetail extends PendingIdentification {
  frameUrl: string;
  videoUrl: string;
}

export interface PendingReviewsResponse {
  data: PendingIdentification[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface ResolveParams {
  id: string;
  action: 'select_operator' | 'skip';
  operatorId?: string;
  notes?: string;
}

// Query keys
export const pendingReviewKeys = {
  all: ['pending-identifications'] as const,
  lists: () => [...pendingReviewKeys.all, 'list'] as const,
  list: (params: { page?: number; limit?: number }) =>
    [...pendingReviewKeys.lists(), params] as const,
  details: () => [...pendingReviewKeys.all, 'detail'] as const,
  detail: (id: string) => [...pendingReviewKeys.details(), id] as const,
  count: () => [...pendingReviewKeys.all, 'count'] as const,
};

/**
 * Fetch paginated pending identifications
 */
export function usePendingReviews(params?: { page?: number; limit?: number }) {
  const api = useApi();
  const page = params?.page ?? 1;
  const limit = params?.limit ?? 12;

  return useQuery({
    queryKey: pendingReviewKeys.list({ page, limit }),
    queryFn: async () => {
      // API client already unwraps { success, data } -> data
      // So we receive { data: [...], pagination: {...} } directly
      return api.get<PendingReviewsResponse>(
        `/api/pending-identifications?page=${page}&limit=${limit}`
      );
    },
  });
}

/**
 * Fetch single pending identification detail
 */
export function usePendingReviewDetail(id: string | null) {
  const api = useApi();

  return useQuery({
    queryKey: pendingReviewKeys.detail(id ?? ''),
    queryFn: () => api.get<PendingIdentificationDetail>(
      `/api/pending-identifications/${id}`
    ),
    enabled: !!id,
  });
}

/**
 * Fetch count of pending identifications (for badge)
 */
export function usePendingReviewCount() {
  const api = useApi();

  return useQuery({
    queryKey: pendingReviewKeys.count(),
    queryFn: async () => {
      // API returns { success, data: { count } }, client unwraps to { count }
      const response = await api.get<{ count: number }>(
        '/api/pending-identifications/count'
      );
      return response.count;
    },
    staleTime: 30000, // 30 seconds - badge can be slightly stale
    refetchInterval: 60000, // Refresh every minute
  });
}

/**
 * Resolve a pending identification
 */
export function useResolvePendingReview() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, action, operatorId, notes }: ResolveParams) => {
      // API client already unwraps { success, data } -> data
      return api.post<{
        faceIdentificationId: string;
        assessmentId?: string;
        message: string;
      }>(`/api/pending-identifications/${id}/resolve`, {
        action,
        operatorId,
        notes,
      });
    },
    onSuccess: () => {
      // Invalidate all pending review queries to refresh the list and count
      queryClient.invalidateQueries({ queryKey: pendingReviewKeys.all });
    },
  });
}
